import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router-dom';
import LandingPage from './components/LandingPage'
import PrivacyPolicyPage from './components/PrivacyPolicy';
import ProfilePage from './components/Profile';
import PassportPage from './components/PassportPage';
import TermsOfServicePage from './components/TermsOfService';
import ProPage from './components/ProPage';
import AboutPage from './components/AboutPage';
import CompatibilityPage from './components/CompatibilityPage';
import ResearchPage from './components/ResearchPage';
import BlogPage from './components/BlogPage';
import BlogPostPage from './components/BlogPostPage';
import DestinationsPage from './components/DestinationsPage';
import DestinationPostPage from './components/DestinationPostPage';
import MagicLinkRedirect from './components/MagicLinkRedirect';
import LocaleLayout from './components/common/LocaleLayout';
import { LOCALES, DEFAULT_LOCALE } from './i18n/config';

// Marketing/legal pages get a localized route under every supported locale
// (e.g. /de/pro, /fr/about). English lives at the unprefixed root. The blog
// is intentionally excluded - it stays English-only, registered separately
// below with no locale variants.
const MARKETING_ROUTES: { path: string; element: React.ReactElement }[] = [
  { path: '/', element: <LandingPage /> },
  { path: '/pro', element: <ProPage /> },
  { path: '/about', element: <AboutPage /> },
  { path: '/compatibility', element: <CompatibilityPage /> },
  { path: '/research', element: <ResearchPage /> },
];

function localizedRoutePath(path: string, lang: string): string {
  if (lang === DEFAULT_LOCALE) return path;
  return path === '/' ? `/${lang}` : `/${lang}${path}`;
}

// Component to handle deep links
const DeepLinkHandler: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleDeepLink = (url: string) => {
      const path = new URL(url).pathname;
      if (path.startsWith('/profile/')) {
        const username = path.split('/').pop();
        navigate(`/profile/${username}`);
      }
    };

    // Check for initial URL when the app loads
    if (window.location.pathname !== '/') {
      handleDeepLink(window.location.href);
    }

    // Listen for changes to the URL
    const handlePopState = () => {
      handleDeepLink(window.location.href);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  return null;
};

function App() {
  return (
    <Router>
      <DeepLinkHandler />
      <Routes>
        {/* Locale-prefixed marketing/legal routes, English included at the root */}
        {LOCALES.flatMap((locale) =>
          MARKETING_ROUTES.map((route) => (
            <Route
              key={`${locale.code}${route.path}`}
              path={localizedRoutePath(route.path, locale.code)}
              element={<LocaleLayout lang={locale.code}>{route.element}</LocaleLayout>}
            />
          ))
        )}

        {/* Never locale-prefixed */}
        <Route path="/profile/:username" element={<ProfilePage />} />
        <Route path="/passport/:handle" element={<PassportPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/destinations" element={<DestinationsPage />} />
        <Route path="/destinations/:slug" element={<DestinationPostPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/auth/magic-link" element={<MagicLinkRedirect />} />
      </Routes>
    </Router>
  )
}

export default App;
